/* import __COLOCATED_TEMPLATE__ from './outbound-chat.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-experience */
import Component from '@glimmer/component';
import type PartGroup from 'embercom/objects/inbox/conversation-stream/part-group';
import type RenderablePart from 'embercom/objects/inbox/renderable-part';
import { type SenderSummary } from 'embercom/objects/inbox/renderable/chat';
import type Chat from 'embercom/objects/inbox/renderable/chat';
import { OutboundInitialPartSenderType } from 'embercom/objects/inbox/outbound-initial-part-sender-types';
import type Session from 'embercom/services/session';
import { inject as service } from '@ember/service';
import type Conversation from 'embercom/objects/inbox/conversation';
import {
  getLanguageFromPartGroup,
  translatedPartExists,
  getAllTranslationLanguageCodesFromPartGroup,
  getOriginalLanguageCodeFromPartGroup,
} from '../helpers/translations';
import { shouldRouteToAutomation } from '../helpers/ai-agent-routing';
import type ConversationTranslationSettings from 'embercom/services/conversation-translation-settings';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'embercom/services/intl';
import { action } from '@ember/object';
import { RenderableType } from 'embercom/models/data/inbox/renderable-types';

interface OutboundChatRenderablePart extends RenderablePart {
  renderableData: Chat;
}

interface OutboundChatPartGroup extends PartGroup {
  parts: Array<OutboundChatRenderablePart>;
}

interface Args {
  partGroup: OutboundChatPartGroup;
  conversation: Conversation;
  isReadOnly: boolean;
}

interface Signature {
  Args: Args;
}

export default class ConversationStreamTeammateContentComponent extends Component<Signature> {
  @service declare session: Session;
  @service declare intl: IntlService;
  @service declare conversationTranslationSettings: ConversationTranslationSettings;

  @tracked showOriginalMessageToggle = true;

  get renderableData() {
    return this.args.partGroup.parts[0].renderableData;
  }

  get isDeleted() {
    return this.renderableData.deletedAt;
  }

  get sender(): SenderSummary {
    return this.isDeleted && !!this.renderableData.adminSummary
      ? this.renderableData.adminSummary
      : this.renderableData.senderSummary;
  }

  get avatarObject() {
    return this.sender;
  }

  private get shouldRouteSenderToAutomation(): boolean {
    return shouldRouteToAutomation(this.session.workspace, this.sender);
  }

  get avatarRoute() {
    if (this.shouldRouteSenderToAutomation) {
      return 'apps.app.settings.ai-automation.automation';
    }
    return 'apps.app.admins.admin';
  }

  get avatarRouteModels() {
    if (this.shouldRouteSenderToAutomation) {
      return [this.session.workspace.id];
    }
    return [this.session.workspace.id, this.sender.id];
  }

  get senderIsTeammate() {
    return this.isDeleted || this.renderableData.senderType === OutboundInitialPartSenderType.Admin;
  }

  get senderIsTeam() {
    return this.renderableData.senderType === OutboundInitialPartSenderType.Team;
  }

  get senderIsBot() {
    return this.renderableData.senderType === OutboundInitialPartSenderType.Bot;
  }

  get lastPartInGroup() {
    let { parts } = this.args.partGroup;
    return parts[parts.length - 1];
  }

  get lastPartSeenByUser() {
    let { conversation } = this.args;

    return (
      conversation?.lastSeenByUserAt &&
      conversation?.lastSeenByUserAt >= this.lastPartInGroup.createdAt
    );
  }

  get autoTranslationEnabled() {
    return this.conversationTranslationSettings.autoTranslationEnabledForConversationObject(
      this.args.conversation,
    );
  }

  get showOriginalMessage(): boolean {
    if (!this.autoTranslationEnabled) {
      return false;
    }
    return this.showOriginalMessageToggle;
  }

  get showTranslatedMessage() {
    return !this.showOriginalMessage;
  }

  get translatedLanguage() {
    return getLanguageFromPartGroup(this.args.partGroup, this.intl.primaryLocale) || '';
  }

  get translatedPartExists() {
    return translatedPartExists(this.args.partGroup);
  }

  get showTranslationQualityFeedback() {
    return this.session.workspace.isFeatureEnabled('translation-quality-feedback');
  }

  get originalLanguageCode(): string | undefined {
    return getOriginalLanguageCodeFromPartGroup(this.args.partGroup);
  }

  get translatedLanguageCode(): string | undefined {
    let original = this.originalLanguageCode;
    return getAllTranslationLanguageCodesFromPartGroup(this.args.partGroup).find(
      (c) => c !== original,
    );
  }

  @action
  toggleTranslation() {
    this.showOriginalMessageToggle = !this.showOriginalMessageToggle;
  }

  isChatType(part: RenderablePart): boolean {
    return part.renderableType === RenderableType.Chat;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationStream::PartGroups::OutboundChat': typeof ConversationStreamTeammateContentComponent;
    'inbox2/conversation-stream/part-groups/outbound-chat': typeof ConversationStreamTeammateContentComponent;
  }
}
